import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  FormProvider,
  useForm,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { ErrorType, FormProps } from "@/types";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
} from "@/services/eventApi";
import { useToast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import EventGeneralInfoForm from "./general-info-form";
import EventAvailabilityForm from "./availability-form";
import EventSlotsAndSchedule from "./slots-and-schedule";
import EventPricingAndRefund from "./pricing-and-refund";
import { format } from "date-fns";
import { EventBookingType } from "@/types/enums";
import { FacilitySchedule, ScheduleType } from "@/types/facility/schedule";
import { cn } from "@/lib/utils";
// Define the shared form props interface for all child components
interface FormStepProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: "add" | "edit";
  open: boolean;
}

const stepContentComponents = [
  EventGeneralInfoForm,
  EventAvailabilityForm,
  EventPricingAndRefund,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? (
    <Component {...formProps} action={formProps.action} open={formProps.open} />
  ) : null;
};

const steps = [
  { label: "General Information", key: 1 },
  { label: "Availability & Schedule", key: 2 },
  { label: "Pricing & Refund", key: 3 },
];

const EventForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<FacilitySchedule>) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const [activeStep, setActiveStep] = useState(1);
  const [hasAvailabilityTimeErrors, setHasAvailabilityTimeErrors] =
    useState(false);
  const [hasOverlapErrors, setHasOverlapErrors] = useState(false);

  // Add default values for all form fields
  const initialValues: FacilitySchedule = {
    org_id: orgId,
    general_info: {
      name: "",
      facility_id: 0,
      tax_group_id: undefined,
      type: ScheduleType.Events,
      color: "",
      max_attendees: undefined,
      break_duration: undefined,
      income_category_id: undefined,
      advance_booking_days: undefined,
      always_available: false,
      is_exclusive: true,
      member_only: false,
      price: undefined,
      duration_per_slot: 0,
      availability: {
        sunday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        monday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        tuesday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        wednesday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        thursday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        friday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
        saturday: {
          is_available: false,
          time_slots: { start_time: "", end_time: "" },
        },
      },
      location: "",
      description: "",
      start_date: format(new Date(), "yyyy-MM-dd"), // YYYY-MM-DD format
      end_date: undefined,
    },
    tier_pricing: [],
    plan_pricing: [],
    group_pricing: [],
    special_pricing: [],
    refund_policies: [], // Default refund policy
    add_ons: [],
  };

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const form = useForm<FacilitySchedule>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  const { toast } = useToast();

  useEffect(() => {
    if (action == "edit" && data) {
      reset(data as FacilitySchedule);
    } else if (action == "add") {
      reset(initialValues);
    }
  }, [action, data, reset]);

  useEffect(() => {
    const handleAvailabilityTimeErrors = (event: any) => {
      setHasAvailabilityTimeErrors(event.detail.hasErrors);
    };
    const handleOverlapStatus = (event: any) => {
      setHasOverlapErrors(event.detail.hasOverlap);
    };

    document.addEventListener(
      "availability-time-errors",
      handleAvailabilityTimeErrors as any,
    );
    document.addEventListener(
      "overlap-status-changed",
      handleOverlapStatus as any,
    );

    return () => {
      document.removeEventListener(
        "availability-time-errors",
        handleAvailabilityTimeErrors as any,
      );
      document.removeEventListener(
        "overlap-status-changed",
        handleOverlapStatus as any,
      );
    };
  }, []);

  const handleClose = () => {
    clearErrors();
    reset(initialValues);
    setData(undefined);
    setActiveStep(1);
    setOpen(false);
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (input: FacilitySchedule) => {
    const payload = { ...input };
    payload.general_info.name = payload.general_info.name?.trim();

    try {
      if (action === "add") {
        await createEvent(payload).unwrap();
        toast({
          variant: "success",
          title: "Event created successfully",
        });
        refetch();
      } else if (action === "edit") {
        await updateEvent({
          ...payload,
          org_id: orgId,
          id: payload.general_info.id as number,
        }).unwrap();
        toast({
          variant: "success",
          title: "Event updated Successfully",
        });
        refetch();
      }
      handleClose();
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail?.replace("schedule", "event") || typedError.data?.message?.replace("schedule", "event")}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleNext = async () => {
    // Check date validation first (before triggering form validation)
    if (activeStep === 1) {
      const startDate = getValues("general_info.start_date");
      const endDate = getValues("general_info.end_date");

      if (startDate && endDate) {
        try {
          const start = new Date(startDate);
          const end = new Date(endDate);

          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            // Normalize dates to compare only date part (set time to midnight)
            const startNormalized = new Date(
              start.getFullYear(),
              start.getMonth(),
              start.getDate(),
            );
            const endNormalized = new Date(
              end.getFullYear(),
              end.getMonth(),
              end.getDate(),
            );
            // Allow same date (start === end), but not start > end
            if (startNormalized > endNormalized) {
              toast({
                variant: "destructive",
                title: "Invalid Date Range",
                description:
                  "Start date cannot be greater than end date. Please correct the dates before proceeding.",
              });
              return;
            }
          }
        } catch (error) {
          // If date parsing fails, let form validation handle it
          console.warn("Date parsing error:", error);
        }
      }
    }

    const isStepValid = await trigger();

    // Check for slot overlaps only if we are on the availability step (step 2)
    if (activeStep === 2 && hasOverlapErrors) {
      toast({
        variant: "destructive",
        title: "Schedule Conflict",
        description:
          "This event timing overlaps with another event/booking. Please adjust your availability settings to resolve conflicts.",
      });
      return;
    }

    // Check for time validation errors from availability form
    if (activeStep === 2 && hasAvailabilityTimeErrors) {
      // Find the first error message to display from `form.formState.errors`
      // This assumes that availability-form.tsx will set errors on the top-level `errors` object as well
      const timeErrors = errors.general_info?.availability as any;
      let errorMessage = "Please fix time validation errors before proceeding.";
      if (timeErrors) {
        for (const dayKey of Object.keys(timeErrors)) {
          const dayErrors = timeErrors[dayKey];
          if (dayErrors.start_time) {
            errorMessage = dayErrors.start_time.message || errorMessage;
            break;
          }
          if (dayErrors.end_time) {
            errorMessage = dayErrors.end_time.message || errorMessage;
            break;
          }
        }
      }

      toast({
        variant: "destructive",
        title: "Time Validation Error",
        description: errorMessage,
      });
      return;
    }

    // Check if at least one day is selected with valid times when on step 2
    if (activeStep === 2) {
      // First, check if duration is filled (required field)
      const { general_info } = getValues();

      if (
        !general_info.duration_per_slot ||
        general_info.duration_per_slot <= 0
      ) {
        toast({
          variant: "destructive",
          title: "Duration Required",
          description: "Please enter a duration per slot before proceeding.",
        });
        return;
      }

      // Trigger form validation for duration_per_slot field
      const isDurationValid = await form.trigger(
        "general_info.duration_per_slot",
      );
      if (!isDurationValid) {
        toast({
          variant: "destructive",
          title: "Duration Validation Error",
          description:
            "Please enter a valid duration per slot before proceeding.",
        });
        return;
      }

      // Check for time validation errors from availability form
      let hasTimeErrors = false;
      let timeErrorDetails: { errors: any; hasErrors: boolean } | null = null;

      // Listen for time errors from availability form
      const checkTimeErrors = () => {
        return new Promise<{ errors: any; hasErrors: boolean }>((resolve) => {
          const handler = (event: any) => {
            resolve(event.detail);
            document.removeEventListener("availability-time-errors", handler);
          };
          document.addEventListener("availability-time-errors", handler);
          // Trigger a check by dispatching a request event
          document.dispatchEvent(new CustomEvent("check-availability-errors"));
          // Timeout after 100ms if no response
          setTimeout(() => {
            document.removeEventListener("availability-time-errors", handler);
            resolve({ errors: {}, hasErrors: false });
          }, 100);
        });
      };

      timeErrorDetails = await checkTimeErrors();
      hasTimeErrors = timeErrorDetails.hasErrors;

      // No longer need this block as we are using hasAvailabilityTimeErrors state from event listener
      // if (hasTimeErrors) {
      //   const firstError = Object.values(timeErrorDetails.errors).find(
      //     (err: any) => err.start_time || err.end_time
      //   ) as { start_time?: string; end_time?: string } | undefined;

      //   const errorMessage = firstError?.start_time || firstError?.end_time || "Please fix time validation errors before proceeding.";

      //   toast({
      //     variant: "destructive",
      //     title: "Time Validation Error",
      //     description: errorMessage,
      //   });
      //   return;
      // }

      // Check if at least one day is selected with valid times
      const availability = getValues("general_info.availability");
      if (availability) {
        const hasValidDay = Object.values(availability).some(
          (day: any) =>
            day?.is_available &&
            day?.time_slots?.start_time &&
            day?.time_slots?.end_time,
        );

        if (!hasValidDay) {
          toast({
            variant: "destructive",
            title: "Availability Required",
            description:
              "Please select at least one day and set its start and end times before proceeding.",
          });
          return;
        }
      } else {
        toast({
          variant: "destructive",
          title: "Availability Required",
          description:
            "Please select at least one day and set its start and end times before proceeding.",
        });
        return;
      }
    }

    // Check for date validation errors
    if (
      activeStep === 1 &&
      (errors.general_info?.start_date || errors.general_info?.end_date)
    ) {
      const startDateError = errors.general_info?.start_date?.message;
      const endDateError = errors.general_info?.end_date?.message;

      if (startDateError || endDateError) {
        toast({
          variant: "destructive",
          title: "Date Validation Error",
          description:
            startDateError ||
            endDateError ||
            "Please correct the date fields before proceeding.",
        });
        return;
      }
    }

    if (isStepValid && !hasOverlapErrors && activeStep < steps.length) {
      setActiveStep(activeStep + 1);
    } else if (hasOverlapErrors) {
      toast({
        variant: "destructive",
        title: "Schedule Conflict",
        description:
          "This event timing overlaps with another event/booking. Please adjust your availability settings to resolve conflicts.",
      });
    }
  };

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const isLastStep = activeStep === steps.length;
  const isFirstStep = activeStep === 1;

  const sheetWidthClass =
    activeStep === 2
      ? "sm:!max-w-full lg:!max-w-[1100px] xl:!max-w-[1320px]"
      : "sm:!max-w-full lg:!max-w-[1000px]";

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className={cn(
          "bg-formbg w-full p-0 custom-scrollbar h-full",
          sheetWidthClass,
        )}
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                  <button type="button" onClick={handleClose} className="shrink-0 mt-0.5">
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div className="min-w-0">
                    <h1 className="font-medium text-sm sm:text-base truncate">
                      {action == "add" ? "Create" : "Edit"} Event
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {!isFirstStep && (
                    <Button
                      type="button"
                      className="h-8 px-2 sm:w-[100px] text-center flex items-center gap-1 sm:gap-2 border-primary text-xs sm:text-sm"
                      variant={"outline"}
                      onClick={handlePrevious}
                    >
                      <i className="fa fa-arrow-left"></i>
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                  )}

                  {!isLastStep ? (
                    <Button
                      type="button"
                      className="h-8 px-3 sm:w-[100px] bg-primary text-black text-center flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  ) : (
                    <LoadingButton
                      type="submit"
                      className="h-8 px-3 sm:w-[120px] bg-primary text-black text-center flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetHeader>

            <div className="min-h-[400px] py-4 px-3 sm:px-6">
              {getStepContent(activeStep, {
                control,
                errors,
                watch,
                setValue,
                trigger,
                action,
                open: isOpen,
              })}
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default EventForm;
